import React, { useEffect, useState } from "react";
import {
  Activity, ArrowRight, ArrowUpRight, BookOpen, Building2, CheckCircle2, Clock,
  FileText, MessageSquare, MoreHorizontal, Send, ShieldCheck, Sparkles, Zap,
} from "lucide-react";

/* ─────────────────────────────────────────────────────────────────────────
   ChainFlow OS — Flow Centre marketing section
   Premium light-mode operational preview · "the operating system for transactions"
   ───────────────────────────────────────────────────────────────────────── */

const useInViewOnce = (rootMargin = "0px 0px -10% 0px") => {
  const [el, setEl] = useState(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!el || inView) return undefined;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); io.disconnect(); } }, { rootMargin });
    io.observe(el);
    return () => io.disconnect();
  }, [el, inView, rootMargin]);
  return [setEl, inView];
};

const easeOut = (t) => 1 - Math.pow(1 - t, 3);
const useCountTo = (target, run, duration = 1400) => {
  const [v, setV] = useState(0);
  useEffect(() => {
    if (!run) return undefined;
    const start = performance.now();
    let raf;
    const step = (now) => {
      const t = Math.min(1, (now - start) / duration);
      setV(target * easeOut(t));
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => raf && cancelAnimationFrame(raf);
  }, [target, run, duration]);
  return v;
};

const fmtAgo = (ms) => {
  const m = Math.floor(ms / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

/* 12-stage canonical progression with current = Mortgage Offer Received (idx 8) */
const STAGES = [
  "Instruction", "Listing", "Viewings", "Offer", "Memo", "AML",
  "Searches", "Enquiries", "Mortgage Offer", "Ready", "Exchange", "Completion",
];
const CURRENT_STAGE = 8;

/* ── Cinematic property visual (SVG, no external image) ────────────────── */
const PropertyVisual = () => (
  <div className="relative w-full h-[148px] rounded-2xl overflow-hidden"
    style={{
      background: "linear-gradient(135deg, #0E1454 0%, #1E1B6B 35%, #4338CA 100%)",
      boxShadow: "0 1px 0 rgba(255,255,255,0.06) inset, 0 18px 42px -22px rgba(67,56,202,0.55)",
    }}>
    {/* atmospheric blobs */}
    <span aria-hidden="true" className="absolute -top-8 -left-8 w-40 h-40 rounded-full blur-3xl pointer-events-none"
      style={{ background: "radial-gradient(circle, rgba(167,139,250,0.45), transparent 70%)" }} />
    <span aria-hidden="true" className="absolute -bottom-10 -right-6 w-36 h-36 rounded-full blur-3xl pointer-events-none"
      style={{ background: "radial-gradient(circle, rgba(99,102,241,0.40), transparent 70%)" }} />
    {/* Layered architectural silhouette */}
    <svg viewBox="0 0 480 148" preserveAspectRatio="xMidYMid slice" className="absolute inset-0 w-full h-full" aria-hidden="true">
      <defs>
        <linearGradient id="bldg-a" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(255,255,255,0.18)" />
          <stop offset="100%" stopColor="rgba(255,255,255,0.04)" />
        </linearGradient>
        <linearGradient id="bldg-b" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(255,255,255,0.10)" />
          <stop offset="100%" stopColor="rgba(255,255,255,0.02)" />
        </linearGradient>
      </defs>
      {/* far skyline */}
      <rect x="22" y="58" width="36" height="90" fill="url(#bldg-b)" rx="2" />
      <rect x="62" y="48" width="50" height="100" fill="url(#bldg-b)" rx="2" />
      <rect x="116" y="62" width="40" height="86" fill="url(#bldg-b)" rx="2" />
      <rect x="350" y="60" width="42" height="88" fill="url(#bldg-b)" rx="2" />
      <rect x="396" y="48" width="38" height="100" fill="url(#bldg-b)" rx="2" />
      <rect x="440" y="68" width="32" height="80" fill="url(#bldg-b)" rx="2" />
      {/* hero tower */}
      <rect x="190" y="22" width="100" height="126" fill="url(#bldg-a)" rx="3"
        style={{ filter: "drop-shadow(0 6px 14px rgba(0,0,0,0.35))" }} />
      {/* hero windows grid */}
      {Array.from({ length: 9 }).map((_, r) => (
        Array.from({ length: 5 }).map((__, c) => {
          const x = 196 + c * 18;
          const y = 32 + r * 12;
          // randomised lit windows for life
          const lit = (r * 5 + c) % 7 === 0 || (r * 5 + c) % 11 === 3;
          return <rect key={`${r}-${c}`} x={x} y={y} width="10" height="6" rx="1"
            fill={lit ? "rgba(253,224,71,0.55)" : "rgba(255,255,255,0.08)"} />;
        })
      ))}
      {/* secondary tower */}
      <rect x="298" y="42" width="44" height="106" fill="url(#bldg-b)" rx="2" />
      {Array.from({ length: 7 }).map((_, r) => (
        Array.from({ length: 3 }).map((__, c) => (
          <rect key={`s-${r}-${c}`} x={304 + c * 12} y={50 + r * 13} width="6" height="5" rx="1" fill="rgba(255,255,255,0.10)" />
        ))
      ))}
    </svg>
    {/* foreground vignette */}
    <span aria-hidden="true" className="absolute inset-0 pointer-events-none"
      style={{ background: "linear-gradient(180deg, transparent 35%, rgba(8,10,32,0.50) 100%)" }} />
    {/* meta chip */}
    <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between text-white">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-violet-200/90">The Gessner</p>
        <p className="text-[14px] font-bold leading-tight mt-0.5">Stratford, London E20</p>
      </div>
      <span className="inline-flex items-center gap-1 px-2 h-6 rounded-md text-[10px] font-bold"
        style={{ background: "rgba(255,255,255,0.14)", border: "1px solid rgba(255,255,255,0.20)", backdropFilter: "blur(8px)" }}>
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 cf-pulse" /> Live
      </span>
    </div>
  </div>
);

/* ── LIVE ACTIVITY (left) ──────────────────────────────────────────────── */
const ACTIVITY_POOL = [
  { tag: "M", text: "Mortgage offer received",        actor: "Lloyds Bank",        tone: "emerald" },
  { tag: "S", text: "Solicitor replied to enquiries", actor: "Hadleigh & Co",      tone: "indigo" },
  { tag: "B", text: "Buyer uploaded ID",              actor: "Mr S. Patel",         tone: "indigo" },
  { tag: "A", text: "AML cleared",                    actor: "Compliance",          tone: "emerald" },
  { tag: "D", text: "Document signed",                actor: "Memorandum of sale",  tone: "indigo" },
  { tag: "V", text: "Survey booked",                  actor: "Mansfield Surveyors", tone: "indigo" },
  { tag: "X", text: "Exchange date proposed",         actor: "Both solicitors",     tone: "violet" },
];
const ACTIVITY_SEED = [
  { tag: "M", text: "Mortgage offer received",        actor: "Lloyds Bank",         tone: "emerald", at: Date.now() - 7  * 60_000 },
  { tag: "S", text: "Solicitor replied to enquiries", actor: "Hadleigh & Co",       tone: "indigo",  at: Date.now() - 38 * 60_000 },
  { tag: "B", text: "Buyer uploaded ID",              actor: "Mr S. Patel",         tone: "indigo",  at: Date.now() - 2 * 3600_000 },
  { tag: "A", text: "AML cleared",                    actor: "Compliance",          tone: "emerald", at: Date.now() - 4 * 3600_000 },
  { tag: "M", text: "Memo issued",                    actor: "Listing agent",       tone: "indigo",  at: Date.now() - 22 * 3600_000 },
];

const LiveActivityPanel = () => {
  const [items, setItems] = useState(ACTIVITY_SEED);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => {
      setItems((prev) => {
        const pick = ACTIVITY_POOL[Math.floor(Math.random() * ACTIVITY_POOL.length)];
        return [{ ...pick, at: Date.now() }, ...prev.slice(0, 4)];
      });
      setTick((n) => n + 1);
    }, 5500);
    return () => clearInterval(t);
  }, []);
  const TONE_BG = { emerald: "rgba(16,185,129,0.10)", indigo: "rgba(99,102,241,0.10)", violet: "rgba(139,92,246,0.10)" };
  const TONE_FG = { emerald: "#059669",                indigo: "#4338CA",                violet: "#7C3AED" };
  return (
    <div className="rounded-3xl p-5 lg:p-6"
      style={{
        background: "linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(248,250,255,0.96) 100%)",
        border: "1px solid rgba(99,102,241,0.10)",
        boxShadow: "0 1px 0 rgba(255,255,255,0.85) inset, 0 24px 50px -28px rgba(15,23,42,0.18)",
      }}
      data-testid="flow-section-live-activity"
    >
      <div className="flex items-center justify-between mb-1">
        <p className="text-[10.5px] font-bold uppercase tracking-[0.20em] text-slate-500">Live activity</p>
        <span className="inline-flex items-center gap-1.5 text-[10.5px] font-semibold text-emerald-600">
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70 cf-pulse" />
            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500" />
          </span>
          Streaming
        </span>
      </div>
      <p className="text-[12.5px] text-slate-500">Last 24 hours · across all chains</p>
      <ul className="mt-5 space-y-3.5" key={`activity-${tick}`}>
        {items.map((a, i) => (
          <li key={`${a.text}-${a.at}`} className={`flex items-center gap-3 ${i === 0 ? "cf-fade-up" : ""}`}>
            <span className="w-9 h-9 rounded-xl text-[11px] font-bold flex items-center justify-center shrink-0"
              style={{ background: TONE_BG[a.tone], color: TONE_FG[a.tone], border: `1px solid ${TONE_BG[a.tone].replace("0.10", "0.22")}` }}>
              {a.tag}
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[12.5px] font-semibold text-slate-900 truncate">{a.text}</p>
              <p className="text-[11px] text-slate-500 truncate">{a.actor}</p>
            </div>
            <span className="text-[10.5px] text-slate-400 tabular-nums">{fmtAgo(Date.now() - a.at)}</span>
          </li>
        ))}
      </ul>
      <button className="mt-5 w-full h-9 rounded-xl text-[11.5px] font-semibold inline-flex items-center justify-center gap-1.5 text-slate-700 transition-colors"
        style={{ background: "rgba(99,102,241,0.04)", border: "1px solid rgba(99,102,241,0.14)" }}>
        View all activity <ArrowRight size={11} />
      </button>
    </div>
  );
};

/* ── ACTIVE DEAL (center) ──────────────────────────────────────────────── */
const ProgressionTimeline = ({ inView }) => {
  // Animate the connector grow on enter
  const grow = useCountTo(CURRENT_STAGE / (STAGES.length - 1), inView, 1600);
  return (
    <div className="relative pt-1 pb-1">
      <div className="absolute left-3 right-3 top-[14px] h-[2px]" style={{ background: "rgba(99,102,241,0.16)" }}>
        <div className="h-full rounded-full" style={{
          width: `${grow * 100}%`,
          background: "linear-gradient(90deg, #6366F1 0%, #4338CA 100%)",
          boxShadow: "0 0 12px rgba(99,102,241,0.45)",
        }} />
      </div>
      <div className="relative grid grid-cols-12 gap-1">
        {STAGES.map((label, i) => {
          const passed = i < CURRENT_STAGE;
          const current = i === CURRENT_STAGE;
          const dotBg = current
            ? "linear-gradient(135deg, #8B5CF6, #4338CA)"
            : passed
              ? "linear-gradient(135deg, #6366F1, #4338CA)"
              : "rgba(255,255,255,1)";
          const dotBorder = current
            ? "1px solid rgba(139,92,246,0.65)"
            : passed
              ? "1px solid rgba(99,102,241,0.55)"
              : "1px solid rgba(99,102,241,0.20)";
          return (
            <div key={label} className="flex flex-col items-center text-center group">
              <span className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold relative z-10"
                style={{
                  background: dotBg,
                  border: dotBorder,
                  color: passed || current ? "#fff" : "#475569",
                  boxShadow: current ? "0 0 0 4px rgba(139,92,246,0.16), 0 0 14px rgba(139,92,246,0.55)" : "none",
                }}>
                {passed ? <CheckCircle2 size={11} strokeWidth={2.6} /> : i + 1}
                {current && (
                  <span aria-hidden="true" className="absolute inset-0 rounded-full cf-pulse" style={{ boxShadow: "0 0 0 6px rgba(139,92,246,0.20)" }} />
                )}
              </span>
              <p className={`text-[8.5px] mt-2 leading-tight ${current ? "text-slate-900 font-bold" : passed ? "text-indigo-600/75" : "text-slate-400"}`}>
                {label}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const Stat = ({ label, value, accent }) => (
  <div>
    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">{label}</p>
    <p className={`font-display text-[22px] font-bold tracking-tight tabular-nums leading-none mt-1 ${accent || "text-slate-900"}`}>
      {value}
    </p>
  </div>
);

const ActiveDealCard = () => {
  const [setRef, inView] = useInViewOnce();
  const completion = useCountTo(67, inView, 1400);
  const exchangeProb = useCountTo(82, inView, 1700);
  return (
    <div ref={setRef}
      className="relative rounded-3xl p-6 lg:p-7"
      style={{
        background: "linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(245,247,255,0.94) 100%)",
        border: "1px solid rgba(99,102,241,0.14)",
        boxShadow: "0 1px 0 rgba(255,255,255,0.85) inset, 0 36px 70px -28px rgba(67,56,202,0.22), 0 14px 30px -16px rgba(15,23,42,0.10)",
      }}
      data-testid="flow-section-active-deal"
    >
      {/* Floating intelligence "WOW" chip */}
      <div className="absolute -top-3 right-5 z-10 flex items-center gap-1.5 h-8 px-3 rounded-full text-[11px] font-bold cf-fade-up"
        style={{
          background: "linear-gradient(180deg, #fff, #FAFBFF)",
          border: "1px solid rgba(16,185,129,0.40)",
          color: "#047857",
          boxShadow: "0 12px 28px -10px rgba(16,185,129,0.45), inset 0 1px 0 rgba(255,255,255,0.85)",
          animationDelay: "300ms",
        }}
        data-testid="flow-section-wow">
        <Sparkles size={11} />
        +£13,200 fee protected
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 cf-pulse" />
      </div>

      {/* Eyebrow + title */}
      <div className="flex items-start justify-between mb-5">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.22em]" style={{ color: "#7C3AED" }}>Active deal</p>
          <h3 className="font-display text-[26px] lg:text-[30px] font-bold text-slate-900 tracking-tight leading-tight mt-1">
            The Gessner, Stratford
          </h3>
          <p className="text-[12px] text-slate-500 mt-1">CF-OCG-2418 · Mortgage offer received · 16 Feb 2026</p>
        </div>
        <button className="w-9 h-9 rounded-xl flex items-center justify-center text-slate-500 hover:text-slate-700 hover:bg-slate-50 transition-colors"
          style={{ background: "rgba(255,255,255,0.6)", border: "1px solid rgba(99,102,241,0.10)" }}>
          <MoreHorizontal size={14} />
        </button>
      </div>

      {/* Property visual */}
      <PropertyVisual />

      {/* Three KPI stats */}
      <div className="grid grid-cols-3 gap-4 mt-6">
        <Stat label="Completion" value={`${Math.round(completion)}%`} />
        <Stat label="Exchange target" value="28 Feb" />
        <Stat label="Fee" value="£18,750" accent="text-indigo-700" />
      </div>

      {/* Progression timeline */}
      <div className="mt-6">
        <ProgressionTimeline inView={inView} />
      </div>

      {/* Intelligence signal */}
      <div className="mt-6 rounded-2xl p-4 flex items-start gap-3"
        style={{ background: "linear-gradient(180deg, rgba(244,63,94,0.04), rgba(244,63,94,0.02))", border: "1px solid rgba(244,63,94,0.18)" }}
        data-testid="flow-section-signal">
        <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: "rgba(244,63,94,0.10)", color: "#E11D48", border: "1px solid rgba(244,63,94,0.22)" }}>
          <Zap size={14} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[12.5px] font-bold text-slate-900">Solicitor inactivity detected · response overdue 3d</p>
          <p className="text-[11.5px] text-slate-500 mt-0.5">ChainFlow auto-chased 2 contacts · escalation prepared</p>
        </div>
        <span className="text-[10px] font-bold uppercase tracking-[0.16em] px-2 h-5 inline-flex items-center rounded"
          style={{ background: "rgba(244,63,94,0.10)", color: "#BE123C", border: "1px solid rgba(244,63,94,0.24)" }}>
          Risk
        </span>
      </div>

      {/* Participants + Exchange probability */}
      <div className="mt-5 flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="flex -space-x-2">
            {["JC", "EH", "ML", "TR"].map((n, i) => (
              <span key={n} className="w-7 h-7 rounded-full text-white text-[9.5px] font-bold flex items-center justify-center ring-2 ring-white"
                style={{ background: `linear-gradient(135deg, hsl(${(i*72)%360},55%,55%), hsl(${(i*72+50)%360},55%,40%))` }}>
                {n}
              </span>
            ))}
          </div>
          <p className="text-[11px] font-semibold text-slate-600">+3 more · all active</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <p className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-slate-500">Exchange probability</p>
          <p className="font-display text-[14px] font-bold text-emerald-600 tabular-nums">{Math.round(exchangeProb)}%</p>
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 cf-pulse" />
        </div>
      </div>

      {/* CTAs */}
      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-2">
        {[
          { label: "Open Deal Room", Icon: ArrowUpRight, primary: true, testid: "flow-cta-deal-room" },
          { label: "View Documents", Icon: FileText,     primary: false, testid: "flow-cta-docs" },
          { label: "Review Timeline", Icon: Clock,        primary: false, testid: "flow-cta-timeline" },
          { label: "Send Update",   Icon: Send,         primary: false, testid: "flow-cta-update" },
        ].map((c) => (
          <button key={c.label}
            data-testid={c.testid}
            className={`group relative h-10 px-3.5 rounded-xl text-[12px] font-bold inline-flex items-center justify-center gap-1.5 transition-all hover:-translate-y-0.5 ${
              c.primary ? "text-white" : "text-slate-700"
            }`}
            style={c.primary
              ? {
                  background: "linear-gradient(135deg, #6366F1 0%, #4338CA 100%)",
                  boxShadow: "0 14px 26px -10px rgba(67,56,202,0.55), inset 0 1px 0 rgba(255,255,255,0.20)",
                }
              : {
                  background: "rgba(255,255,255,0.85)",
                  border: "1px solid rgba(99,102,241,0.16)",
                  boxShadow: "0 4px 12px -6px rgba(15,23,42,0.06)",
                }
            }>
            <c.Icon size={12} strokeWidth={2.4} />
            {c.label}
          </button>
        ))}
      </div>
    </div>
  );
};

/* ── FLOW SIGNALS (right) ──────────────────────────────────────────────── */
const FlowSignalsPanel = () => {
  const [setRef, inView] = useInViewOnce();
  const responseRate  = useCountTo(94,  inView, 1600);
  const completionPct = useCountTo(67,  inView, 1400);
  const docsCleared   = useCountTo(12,  inView, 1100);
  const stalled       = useCountTo(2,   inView, 900);
  return (
    <div ref={setRef}
      className="rounded-3xl p-5 lg:p-6"
      style={{
        background: "linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(248,250,255,0.96) 100%)",
        border: "1px solid rgba(99,102,241,0.10)",
        boxShadow: "0 1px 0 rgba(255,255,255,0.85) inset, 0 24px 50px -28px rgba(15,23,42,0.18)",
      }}
      data-testid="flow-section-flow-signals"
    >
      <div className="flex items-center justify-between mb-1">
        <p className="text-[10.5px] font-bold uppercase tracking-[0.20em] text-slate-500">Flow signals</p>
        <span className="text-[10.5px] font-semibold text-indigo-600 inline-flex items-center gap-1">
          <Activity size={10} /> Live
        </span>
      </div>
      <p className="text-[12.5px] text-slate-500">Operational intelligence · auto-detected</p>

      {/* Big completion ring */}
      <div className="mt-5 flex items-center gap-4">
        <div className="relative w-[80px] h-[80px] shrink-0">
          <svg viewBox="0 0 80 80" className="w-full h-full -rotate-90">
            <circle cx="40" cy="40" r="33" stroke="rgba(99,102,241,0.10)" strokeWidth="6" fill="none" />
            <circle cx="40" cy="40" r="33" stroke="url(#fs-grad)" strokeWidth="6" fill="none" strokeLinecap="round"
              strokeDasharray={`${(completionPct / 100) * (2 * Math.PI * 33)} ${2 * Math.PI * 33}`} />
            <defs>
              <linearGradient id="fs-grad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#6366F1" />
                <stop offset="100%" stopColor="#7C3AED" />
              </linearGradient>
            </defs>
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
            <p className="font-display text-[18px] font-bold text-slate-900 tabular-nums">{Math.round(completionPct)}%</p>
            <p className="text-[8.5px] text-slate-500 mt-0.5">Complete</p>
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[12px] font-bold text-slate-900">Pipeline progressing</p>
          <p className="text-[11px] text-slate-500 mt-0.5">11 days faster than UK avg</p>
          <span className="inline-flex items-center gap-1 px-2 h-5 rounded mt-1.5 text-[9.5px] font-bold uppercase tracking-[0.14em]"
            style={{ background: "rgba(16,185,129,0.10)", color: "#047857", border: "1px solid rgba(16,185,129,0.22)" }}>
            <ShieldCheck size={9} /> Healthy
          </span>
        </div>
      </div>

      {/* Signal chips */}
      <ul className="mt-5 space-y-2.5">
        {[
          { label: "Solicitor response rate", value: `${Math.round(responseRate)}%`, tone: "emerald" },
          { label: "Documents cleared today", value: `${Math.round(docsCleared)}`, tone: "indigo" },
          { label: "Stalled stages",          value: `${Math.round(stalled)}`,     tone: "amber" },
          { label: "Avg time to next step",   value: "1d 4h",                       tone: "slate" },
        ].map((s) => {
          const tone = {
            emerald: { bg: "rgba(16,185,129,0.08)",  bd: "rgba(16,185,129,0.20)",  fg: "#047857" },
            indigo:  { bg: "rgba(99,102,241,0.08)",  bd: "rgba(99,102,241,0.18)",  fg: "#4338CA" },
            amber:   { bg: "rgba(245,158,11,0.08)",  bd: "rgba(245,158,11,0.20)",  fg: "#B45309" },
            slate:   { bg: "rgba(15,23,42,0.04)",    bd: "rgba(15,23,42,0.08)",    fg: "#475569" },
          }[s.tone];
          return (
            <li key={s.label} className="flex items-center justify-between rounded-xl px-3 py-2.5"
              style={{ background: tone.bg, border: `1px solid ${tone.bd}` }}>
              <p className="text-[11.5px] font-semibold text-slate-700">{s.label}</p>
              <p className="text-[12.5px] font-bold tabular-nums" style={{ color: tone.fg }}>{s.value}</p>
            </li>
          );
        })}
      </ul>

      <button className="mt-5 w-full h-9 rounded-xl text-[11.5px] font-semibold inline-flex items-center justify-center gap-1.5 text-slate-700 transition-colors"
        style={{ background: "rgba(99,102,241,0.04)", border: "1px solid rgba(99,102,241,0.14)" }}>
        Open intelligence <ArrowRight size={11} />
      </button>
    </div>
  );
};

/* ── BOTTOM METRICS BAND ───────────────────────────────────────────────── */
const MetricsBand = () => {
  const [setRef, inView] = useInViewOnce();
  const m = [
    { value: useCountTo(56, inView, 1500), suffix: "%", label: "Fall-Through Risk Reduction" },
    { value: useCountTo(34, inView, 1500), suffix: "%", label: "Faster Exchange Speed" },
    { value: useCountTo(100, inView, 1500), suffix: "%", label: "Client Transparency" },
    { value: useCountTo(36, inView, 1500), suffix: "×", label: "Team Visibility", divisor: 10 },
    { value: useCountTo(47, inView, 1500), suffix: "%", label: "Operational Efficiency" },
  ];
  return (
    <div ref={setRef}
      className="mt-6 lg:mt-8 grid grid-cols-2 md:grid-cols-5 gap-2 lg:gap-3 rounded-3xl p-5 lg:p-6"
      style={{
        background: "linear-gradient(180deg, rgba(245,247,255,0.85) 0%, rgba(238,242,255,0.65) 100%)",
        border: "1px solid rgba(99,102,241,0.10)",
        boxShadow: "0 1px 0 rgba(255,255,255,0.85) inset",
      }}
      data-testid="flow-section-metrics"
    >
      {m.map((s, i) => {
        const display = s.divisor ? (s.value / s.divisor).toFixed(1) : Math.round(s.value);
        return (
          <div key={s.label} className="text-center px-2">
            <p className="font-display text-[28px] lg:text-[34px] font-bold tracking-tight text-slate-900 tabular-nums leading-none">
              {display}<span className="text-[18px] lg:text-[22px] font-bold text-indigo-600 ml-0.5">{s.suffix}</span>
            </p>
            <p className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-slate-500 mt-2 leading-tight">{s.label}</p>
          </div>
        );
      })}
    </div>
  );
};

/* ── SECTION ───────────────────────────────────────────────────────────── */
export const FlowCentreSection = () => (
  <section
    className="max-w-[1280px] mx-auto px-6 lg:px-10 pb-20 lg:pb-28 relative"
    data-testid="flow-centre-section"
  >
    {/* Atmospheric backdrop */}
    <span aria-hidden="true" className="absolute -inset-x-6 -inset-y-10 -z-10 rounded-[40px] blur-3xl pointer-events-none"
      style={{ background: "radial-gradient(ellipse at center, rgba(99,102,241,0.10) 0%, rgba(124,58,237,0.06) 40%, transparent 75%)" }} />

    {/* Eyebrow + title */}
    <div className="text-center mb-10 lg:mb-14">
      <p className="text-[10.5px] font-bold uppercase tracking-[0.32em] inline-flex items-center justify-center gap-2"
        style={{ color: "rgba(67,56,202,0.78)" }}>
        <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 cf-pulse" />
        Live Transaction Operating System
      </p>
      <h2 className="font-display font-bold tracking-[-0.025em] text-slate-900 mt-4"
        style={{ fontSize: "clamp(40px, 6vw, 68px)", lineHeight: 1.02 }}>
        Flow Centre
      </h2>
      <p className="text-[15px] lg:text-[17px] text-slate-500 mt-4 max-w-xl mx-auto">
        The live operating layer for property transactions.
      </p>
    </div>

    {/* 3-col operational layout */}
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr_1fr] gap-4 lg:gap-5">
      <LiveActivityPanel />
      <ActiveDealCard />
      <FlowSignalsPanel />
    </div>

    {/* Bottom metrics band */}
    <MetricsBand />
  </section>
);

export default FlowCentreSection;
